import React, { useContext } from 'react';
import cssClass from './Sidebar.module.css';
import componentIcon from '../../assets/components-icon.svg';
import predefinedModuleIcon from '../../assets/predefined-module-icon.svg';
import arrangeOrderIcon from '../../assets/arrange-order-icon.svg';
import componentIconActive from '../../assets/components-icon-active.svg';
import predefinedModuleIconActive from '../../assets/predefined-module-icon-active.svg';
import arrangeOrderIconActive from '../../assets/arrange-order-icon-active.svg';
import settingsIcon from '../../assets/settings-module-icon.svg';
import ComponentSection from './sidebar-sections/ComponentSection';
import PredefinedModuleSection from './sidebar-sections/PredefinedModuleSection';
import ModuleSequencingSection from './sidebar-sections/ModuleSequencingSection';
import { SettingsSection } from './sidebar-sections/SettingsSection';
import { Tooltip } from 'react-tooltip';
import { SidebarContext } from '../../context/SidebarContext';
import { AnimatePresence, motion } from 'framer-motion';

// Animation Variants
const modalVariants = {
  hidden: {
    x: -200,
    zIndex: -1,
    transition: {
      type: "tween",
      duration: 0.3,
      ease: "easeInOut",
    },
  },
  visible: {
    x: 0,
    zIndex: -1,
    transition: {
      type: "tween",
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

/**
 * Sidebar component that allows users to navigate between different sections.
 *
 * The sidebar includes three sections:
 * - **Components**
 * - **Predefined Modules**
 * - **Rearrange Modules**
 *
 * Each section has an associated icon and tooltip. Clicking a section toggles its content.
 *
 * @component
 *
 * @example
 * return <Sidebar />;
 *
 * @returns {JSX.Element} The rendered Sidebar component.
 */
const Sidebar = () => {
  const { activeSection, setActiveSection } = useContext(SidebarContext);

  /**
   * List of available sidebar sections with their corresponding icons and components.
   */
  const availableSections = [
    {
      name: 'components-section',
      normalIcon: componentIcon,
      activeIcon: componentIconActive,
      component: ComponentSection,
      toolTip: {
        content: 'Components',
        place: 'right',
      },
    },
    {
      name: 'predefined-module-section',
      normalIcon: predefinedModuleIcon,
      activeIcon: predefinedModuleIconActive,
      component: PredefinedModuleSection,
      toolTip: {
        content: 'Predefined Modules',
        place: 'right',
      },
    },
    {
      name: 'arrange-order-section',
      normalIcon: arrangeOrderIcon,
      activeIcon: arrangeOrderIconActive,
      component: ModuleSequencingSection,
      toolTip: {
        content: 'Rearrange Modules',
        place: 'right',
      },
    },
    {
      name: 'settings-section',
      normalIcon: settingsIcon,
      activeIcon: settingsIcon,
      component: SettingsSection,
      toolTip: {
        content: 'Settings',
        place: 'right',
      },
    },
  ];

  /**
   * Changes or toggles the active sidebar section.
   *
   * @param {Object} section - The section object to activate or deactivate.
   */
  const changeOrToggleActiveSection = (section) => {
    if (activeSection && section.name === activeSection?.name) {
      setActiveSection(null);
    } else {
      setActiveSection(section);
    }
  };

  return (
    <>
      <div className={cssClass['main-container']}>
        {/* Small sidebar with section icons */}
        <div className={cssClass['sidebar-small']}>
          <div className={cssClass['sections-container']}>
            {availableSections.map((section, index) => (
              <div
                key={index}
                className={`${cssClass['section']}`}
                onClick={() => changeOrToggleActiveSection(section)}
              >
                <img
                  data-tooltip-id="sidebar-tooltip"
                  data-tooltip-content={section.toolTip.content}
                  data-tooltip-place={section.toolTip.place}
                  className={cssClass['section-image']}
                  src={
                    activeSection?.name === section.name
                      ? section.activeIcon
                      : section.normalIcon
                  }
                  alt={section.name}
                />

                {activeSection?.name === section.name && (
                  <motion.div
                    layoutId="active-section"
                    className={cssClass['active-section']}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

      {/* Render selected section content */}
        <AnimatePresence mode="wait">
          <motion.div className={cssClass['section-content']} variants={modalVariants} initial='hidden' animate='visible' exit='hidden' key={activeSection?.name || 'component-section'}>
              {activeSection && <activeSection.component />}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Tooltip for sidebar icons */}
      <Tooltip
        id="sidebar-tooltip"
        style={{
          backgroundColor: '#000000',
          color: '#FFFFFF',
          fontFamily: 'Arial',
          fontSize: '10px',
          fontWeight: '500',
          textAlign: 'center',
          // borderRadius: '20px',
          padding: '5px',
          zIndex: 1000,
        }}
        noArrow
        className={cssClass['custom-tooltip-animation']}
      />
    </>
  );
};

export default Sidebar;
